import { useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import {
  ButtonLink,
  IconBadge,
  PageHero,
  SectionHeading,
  Seo,
  Tag,
  useLocale,
} from "../components/Layout";
import {
  caseStudies,
  companies,
  faqs,
  insights,
  legalPages,
  solutions,
  stats,
  values,
} from "../data/siteData";

function DarkCta({
  eyebrow = "Let’s move forward",
  title = "Have a challenge worth solving?",
  description = "Tell us what you are building, changing, or improving. We will help you find the right next step.",
  to = "/contact",
  label = "Start a conversation",
}) {
  return (
    <section className="dark-cta">
      <div className="container dark-cta-inner">
        <div>
          <span className="eyebrow eyebrow-light">{eyebrow}</span>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <ButtonLink to={to} variant="light">
          {label}
        </ButtonLink>
      </div>
    </section>
  );
}

function ProcessSteps({ steps }) {
  return (
    <div className="process-grid">
      {steps.map((step, index) => (
        <div className="process-step" key={step.title || step}>
          <span className="process-number">0{index + 1}</span>
          <div>
            <h3>{step.title || step}</h3>
            {step.description && <p>{step.description}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function CompanyCard({ company, featured = false }) {
  return (
    <article
      className={`company-card ${featured ? "company-card-featured" : ""}`}
      style={{ "--accent": company.color, "--accent-soft": company.softColor }}
    >
      <div className="company-card-top">
        <IconBadge name={company.icon} color={company.slug === "qubosoft" ? "blue" : company.slug === "quboinfra" ? "green" : "orange"} />
        <span className="card-arrow">
          <Icon name="arrow" size={17} />
        </span>
      </div>
      <span className="eyebrow" style={{ color: company.color }}>
        {company.eyebrow}
      </span>
      <h3>{company.name}</h3>
      <p className="company-card-tagline">{company.tagline}</p>
      <p>{company.description}</p>
      <div className="tag-list">
        {company.capabilities.slice(0, 3).map((capability) => (
          <Tag key={capability} color="neutral">
            {capability}
          </Tag>
        ))}
      </div>
      <Link className="text-link" to={`/companies/${company.slug}`}>
        Explore {company.name}
        <Icon name="arrow" size={16} />
      </Link>
    </article>
  );
}

function SolutionCard({ solution, featured = false }) {
  return (
    <article
      className={`solution-card ${featured ? "solution-card-featured" : ""}`}
      style={{ "--accent": solution.color }}
    >
      <IconBadge
        name={solution.icon}
        color={
          solution.slug === "technology"
            ? "blue"
            : solution.slug === "infrastructure"
              ? "green"
              : solution.slug === "commerce"
                ? "orange"
                : "purple"
        }
        size={featured ? "large" : "normal"}
      />
      <span className="eyebrow" style={{ color: solution.color }}>
        {solution.eyebrow}
      </span>
      <h3>{solution.name}</h3>
      <p>{solution.description}</p>
      <ul className="check-list">
        {solution.points.map((point) => (
          <li key={point}>
            <Icon name="check" size={16} />
            {point}
          </li>
        ))}
      </ul>
      <Link className="text-link" to={solution.link}>
        Explore solution
        <Icon name="arrow" size={16} />
      </Link>
    </article>
  );
}

export function HomePage() {
  const { copy } = useLocale();

  return (
    <>
      <Seo
        title="Software, infrastructure, commerce & AI"
        description="QuboGroup helps ambitious businesses move forward across software, infrastructure, commerce, and AI."
      />
      <section className="home-hero">
        <div className="container home-hero-grid">
          <div className="home-hero-copy">
            <span className="eyebrow eyebrow-light">QuboGroup / One group, many possibilities</span>
            <h1>
              Build what&apos;s next with a partner who sees the{" "}
              <span className="text-gradient">whole picture.</span>
            </h1>
            <p>
              We bring together technology, infrastructure, commerce, and AI to
              help ambitious teams turn opportunity into useful progress.
            </p>
            <div className="hero-actions">
              <ButtonLink to="/solutions">{copy.common.explore} solutions</ButtonLink>
              <ButtonLink to="/about" variant="ghost-light">
                Why QuboGroup
              </ButtonLink>
            </div>
            <div className="hero-proof">
              <span className="proof-avatars" aria-hidden="true">
                <i>Q</i>
                <i>3</i>
                <i>+</i>
              </span>
              <span>
                <strong>One connected team</strong>
                <small>Across four solution pathways</small>
              </span>
            </div>
          </div>

          <div className="home-hero-visual" aria-label="QuboGroup connects software, infrastructure, commerce and AI">
            <div className="hero-visual-glow" />
            <div className="hero-grid-lines" />
            <div className="hero-orbit hero-orbit-large" />
            <div className="hero-orbit hero-orbit-small" />
            <div className="hero-central">
              <span>Q</span>
              <small>GROUP</small>
            </div>
            <div className="hero-node hero-node-soft">
              <Icon name="code" size={20} />
              <span>Soft</span>
            </div>
            <div className="hero-node hero-node-infra">
              <Icon name="building" size={20} />
              <span>Infra</span>
            </div>
            <div className="hero-node hero-node-kart">
              <Icon name="cart" size={20} />
              <span>Kart</span>
            </div>
            <div className="hero-node hero-node-ai">
              <Icon name="brain" size={20} />
              <span>AI</span>
            </div>
            <span className="hero-star star-one">+</span>
            <span className="hero-star star-two">+</span>
            <span className="hero-star star-three">+</span>
          </div>
        </div>
        <div className="container hero-bottom-row">
          <span>Software</span>
          <span>Infrastructure</span>
          <span>Commerce</span>
          <span>AI-ready thinking</span>
        </div>
      </section>

      <section className="section section-tight">
        <div className="container">
          <div className="intro-row">
            <div>
              <span className="eyebrow">The Qubo advantage</span>
              <h2>One group. The right specialist for the job.</h2>
            </div>
            <p>
              Complex challenges rarely stay inside one category. Our brands
              keep their specialist focus while giving you one clear partner
              across the work.
            </p>
          </div>
          <div className="company-grid">
            {companies.map((company) => (
              <CompanyCard company={company} key={company.slug} />
            ))}
          </div>
        </div>
      </section>

      <section className="section section-surface">
        <div className="container ai-home-feature">
          <div className="ai-feature-copy">
            <span className="eyebrow eyebrow-purple">A new way to move forward</span>
            <h2>Make AI useful, responsible, and close to the work.</h2>
            <p>
              The best AI experiences are not magic tricks. They help people
              find the right context, remove repeatable friction, and make
              confident decisions with the right human checks in place.
            </p>
            <ul className="check-list check-list-dark">
              <li><Icon name="check" size={16} />Knowledge assistants grounded in your context</li>
              <li><Icon name="check" size={16} />Workflow automation with people in the loop</li>
              <li><Icon name="check" size={16} />Document and data intelligence that teams can trust</li>
            </ul>
            <ButtonLink to="/solutions/ai" variant="dark">
              Explore AI solutions
            </ButtonLink>
          </div>
          <div className="ai-feature-media">
            <img
              src="/images/ai-solutions-hero.jpg"
              alt="Abstract blue connected cubes representing an AI system"
            />
            <div className="media-caption">
              <Icon name="brain" size={18} />
              <span>AI solutions / Built around useful outcomes</span>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHeading
            eyebrow="How we work"
            title="Clarity first. Momentum next."
            description="A simple, transparent path from a promising idea to a dependable result."
          />
          <ProcessSteps
            steps={[
              {
                title: "Discover",
                description: "We understand the outcome, people, constraints, and opportunity before proposing a solution.",
              },
              {
                title: "Design",
                description: "We make the experience, scope, and delivery plan visible so the right decisions happen early.",
              },
              {
                title: "Deliver",
                description: "We work in focused milestones, communicate clearly, and keep improving after launch or handover.",
              },
            ]}
          />
        </div>
      </section>

      <DarkCta
        eyebrow="Start with a conversation"
        title="Have a big idea or a messy challenge?"
        description="Bring us the context. We will help you see the path, the people, and the capabilities it will take to move forward."
      />
    </>
  );
}

export function SolutionsPage() {
  return (
    <>
      <Seo
        title="Solutions"
        description="Explore QuboGroup solutions across technology, infrastructure, commerce, and AI."
      />
      <PageHero
        eyebrow="What we can do together"
        title="Solutions designed around the outcome."
        description="Choose a focused capability or bring several Qubo specialists together. Either way, the work starts with what needs to change."
        accent="blue"
      >
        <ButtonLink to="/contact">Talk through your challenge</ButtonLink>
        <ButtonLink to="/solutions/ai" variant="ghost-light">
          Explore AI solutions
        </ButtonLink>
      </PageHero>

      <section className="section section-tight">
        <div className="container">
          <SectionHeading
            eyebrow="Four pathways"
            title="Specialist capabilities, connected thinking."
            description="Every solution is grounded in the people, systems, and real-world conditions it needs to serve."
          />
          <div className="solution-grid">
            {solutions.map((solution) => (
              <SolutionCard solution={solution} featured={solution.slug === "ai"} key={solution.slug} />
            ))}
          </div>
        </div>
      </section>

      <section className="section section-surface">
        <div className="container split-section">
          <div className="split-copy">
            <span className="eyebrow">One partner, less friction</span>
            <h2>Some challenges need more than one lens.</h2>
            <p>
              A digital product may depend on physical operations. A new
              facility may need better systems. A commerce experience may need
              clearer data. We help the pieces work together without losing
              the expertise each one needs.
            </p>
            <ButtonLink to="/one-team-three-brands" variant="dark">
              See how the group connects
            </ButtonLink>
          </div>
          <div className="connection-map" aria-label="Connection between QuboGroup solution areas">
            <div className="connection-center">
              <Icon name="spark" size={24} />
              <strong>QuboGroup</strong>
              <span>Shared direction</span>
            </div>
            <div className="connection-node connection-node-one"><Icon name="code" size={19} />Technology</div>
            <div className="connection-node connection-node-two"><Icon name="building" size={19} />Infrastructure</div>
            <div className="connection-node connection-node-three"><Icon name="cart" size={19} />Commerce</div>
            <div className="connection-node connection-node-four"><Icon name="brain" size={19} />AI</div>
            <span className="connection-line line-one" />
            <span className="connection-line line-two" />
            <span className="connection-line line-three" />
            <span className="connection-line line-four" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHeading
            eyebrow="From first conversation to first result"
            title="A delivery model that stays clear."
            description="You always know what we are learning, making, and measuring next."
          />
          <ProcessSteps
            steps={[
              { title: "Frame the opportunity", description: "Align on the problem, desired result, audience, and constraints." },
              { title: "Choose the right mix", description: "Bring in the specialist company or combination of capabilities the work needs." },
              { title: "Make progress visible", description: "Use focused milestones, clear ownership, and regular decisions to keep momentum." },
              { title: "Build for what is next", description: "Leave you with a solution and the confidence to keep improving it." },
            ]}
          />
        </div>
      </section>

      <DarkCta title="Not sure which pathway fits?" description="That is exactly what the first conversation is for. We can help map the right starting point." />
    </>
  );
}

export function AiSolutionsPage() {
  return (
    <>
      <Seo
        title="AI Solutions"
        description="Useful, responsible AI solutions for knowledge, workflows, documents, and better decisions."
        image="/images/ai-solutions-hero.jpg"
      />
      <PageHero
        eyebrow="QuboSoft / AI solutions"
        title="Turn AI potential into useful progress."
        description="We help teams apply AI to the work that matters: finding context, reducing repeatable effort, and making better decisions without losing human judgement."
        image="/images/ai-solutions-hero.jpg"
        imageAlt="Abstract blue connected cubes representing a structured AI system"
        accent="purple"
      >
        <ButtonLink to="/contact">Plan an AI conversation</ButtonLink>
        <ButtonLink to="/insights/a-practical-starting-point-for-ai" variant="ghost-light">
          Read our starting point
        </ButtonLink>
      </PageHero>

      <section className="section section-tight">
        <div className="container">
          <div className="ai-principle-banner">
            <div className="ai-principle-icon"><Icon name="brain" size={26} /></div>
            <div>
              <span className="eyebrow eyebrow-purple">Our point of view</span>
              <h2>AI should feel like a clearer way to work.</h2>
              <p>
                That means the system understands the context, the team knows
                when to trust it, and the next action becomes easier to see.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-surface">
        <div className="container">
          <SectionHeading
            eyebrow="Where we start"
            title="AI use cases with a job to do."
            description="We look for practical opportunities that can be measured, improved, and adopted by the people doing the work."
          />
          <div className="ai-use-case-grid">
            {[
              { icon: "layers", title: "Knowledge assistants", description: "Help teams search, summarise, and ask questions across the information they already use." },
              { icon: "clock", title: "Workflow automation", description: "Reduce repetitive hand-offs and give people more time for judgement, relationships, and craft." },
              { icon: "shield", title: "Document intelligence", description: "Extract, classify, compare, and route information from documents with reviewable outputs." },
              { icon: "chart", title: "Decision support", description: "Bring the right signals together so teams can see patterns, options, and next steps sooner." },
            ].map((item) => (
              <article className="use-case-card" key={item.title}>
                <IconBadge name={item.icon} color="purple" />
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <span className="use-case-arrow"><Icon name="arrow" size={17} /></span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container ai-delivery-layout">
          <div>
            <span className="eyebrow">A grounded delivery process</span>
            <h2>Start small. Learn quickly. Scale with confidence.</h2>
            <p>
              AI initiatives work best when they earn their next step. We
              define the use case, protect the context, test the experience,
              and measure whether it actually helps.
            </p>
          </div>
          <ProcessSteps
            steps={[
              { title: "Find the friction", description: "Choose a real workflow with a clear user and a visible cost of doing nothing." },
              { title: "Prepare the context", description: "Review data, permissions, quality, and the boundaries the system needs." },
              { title: "Prototype the experience", description: "Make the interaction tangible and test it with the people who will use it." },
              { title: "Measure and improve", description: "Track usefulness, accuracy, adoption, and risk before expanding the scope." },
            ]}
          />
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <SectionHeading
            eyebrow="Built with care"
            title="Responsible by design."
            description="Useful AI depends on more than a good model. It depends on clear boundaries and a thoughtful experience."
            light
          />
          <div className="guardrail-grid">
            {[
              { icon: "shield", title: "Protect the context", text: "Use the right access controls and keep sensitive information in the right hands." },
              { icon: "users", title: "Keep people involved", text: "Make review, correction, and human judgement easy wherever the decision matters." },
              { icon: "chart", title: "Measure the outcome", text: "Track whether the solution saves time, improves quality, or makes a decision clearer." },
            ].map((item) => (
              <article className="guardrail-card" key={item.title}>
                <IconBadge name={item.icon} color="light" />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <DarkCta
        eyebrow="Ready when you are"
        title="Have an AI idea? Make it concrete."
        description="Bring us the workflow, the question, or the friction. We will help you decide whether AI is the right answer—and what a sensible first step looks like."
        label="Discuss an AI use case"
      />
    </>
  );
}

export function CompaniesPage() {
  return (
    <>
      <Seo
        title="Our Companies"
        description="Meet QuboSoft, QuboInfra, and QuboKart—three specialist companies connected by one group vision."
      />
      <PageHero
        eyebrow="One group / three specialist companies"
        title="Different strengths. Shared direction."
        description="QuboSoft, QuboInfra, and QuboKart each focus on a distinct domain while sharing the same commitment to useful work, clear delivery, and lasting value."
        accent="blue"
      >
        <ButtonLink to="/work-with-qubo">Work with Qubo</ButtonLink>
      </PageHero>
      <section className="section section-tight">
        <div className="container">
          <div className="company-grid company-grid-large">
            {companies.map((company) => (
              <CompanyCard company={company} featured key={company.slug} />
            ))}
          </div>
        </div>
      </section>
      <section className="section section-surface">
        <div className="container split-section">
          <div className="split-copy">
            <span className="eyebrow">How the group works</span>
            <h2>Specialist enough to go deep. Connected enough to see the whole.</h2>
            <p>
              You can engage one company for a focused need or bring the group
              together when the challenge crosses domains. The relationship
              stays simple, while the expertise stays specific.
            </p>
            <ButtonLink to="/one-team-three-brands" variant="dark">
              Explore our shared approach
            </ButtonLink>
          </div>
          <div className="group-stat-grid">
            {stats.map((stat) => (
              <div className="group-stat" key={stat.label}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <DarkCta title="Know which specialist you need?" description="Go straight to a company page, or tell us the outcome and we will help you find the right entry point." />
    </>
  );
}

export function CompanyDetailPage() {
  const { slug } = useParams();
  const company = companies.find((item) => item.slug === slug);

  if (!company) {
    return <Navigate to="/companies" replace />;
  }

  const accentName =
    company.slug === "qubosoft" ? "blue" : company.slug === "quboinfra" ? "green" : "orange";

  return (
    <div className="company-detail" style={{ "--brand": company.color, "--brand-soft": company.softColor }}>
      <Seo title={company.name} description={company.description} image={company.image || "/images/qg-logo.png"} />
      <PageHero
        eyebrow={company.eyebrow}
        title={company.tagline}
        description={company.description}
        image={company.image}
        imageAlt={`${company.name} ${company.focus}`}
        accent={accentName}
      >
        <ButtonLink to="/contact">Talk to {company.name}</ButtonLink>
        <ButtonLink to="/companies" variant="ghost-light">
          All companies
        </ButtonLink>
      </PageHero>

      <section className="section section-tight">
        <div className="container company-detail-intro">
          <div>
            <span className="eyebrow" style={{ color: company.color }}>{company.name}</span>
            <h2>Focused on {company.focus.toLowerCase()} that works in the real world.</h2>
          </div>
          <p>{company.audience}</p>
        </div>
      </section>

      <section className="section section-surface">
        <div className="container">
          <SectionHeading
            eyebrow="Capabilities"
            title={`What ${company.name} brings to the table.`}
            description="A focused set of capabilities, brought together around the problem in front of you."
          />
          <div className="capability-grid">
            {company.capabilities.map((capability, index) => (
              <article className="capability-card" key={capability}>
                <span className="capability-index">0{index + 1}</span>
                <Icon name={company.icon} size={22} />
                <h3>{capability}</h3>
                <p>
                  Practical expertise shaped around clear outcomes, thoughtful
                  decisions, and a delivery process you can see.
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container company-approach-layout">
          <div>
            <span className="eyebrow">Our approach</span>
            <h2>Good work gets easier when the next step is clear.</h2>
            <p>
              We make the work visible, keep communication direct, and build
              enough feedback into the process to catch the important things
              early.
            </p>
            {company.website && (
              <ButtonLink to={company.website} external variant="outline">
                Visit {company.name}
              </ButtonLink>
            )}
          </div>
          <ProcessSteps steps={company.approach.map((step) => ({ title: step }))} />
        </div>
      </section>

      <DarkCta
        eyebrow={`${company.name} / Let’s talk`}
        title={`Have a ${company.focus.toLowerCase()} challenge?`}
        description="Share a little context and we will connect you with the right people for a useful first conversation."
        label={`Start with ${company.name}`}
      />
    </div>
  );
}

export function CaseStudiesPage() {
  return (
    <>
      <Seo
        title="Case Studies & Capabilities"
        description="Explore the kinds of technology, infrastructure, commerce, and AI outcomes QuboGroup is built to deliver."
      />
      <PageHero
        eyebrow="Capabilities in action"
        title="The work is different. The standard stays high."
        description="A growing library of representative engagement patterns across the QuboGroup ecosystem. We will keep adding verified work and outcomes here."
        accent="blue"
      >
        <ButtonLink to="/contact">Discuss your project</ButtonLink>
      </PageHero>
      <section className="section section-tight">
        <div className="container">
          <div className="case-study-grid">
            {caseStudies.map((study) => (
              <article className="case-study-card" style={{ "--accent": study.color }} key={study.title}>
                <div className="case-study-icon"><Icon name={study.icon} size={25} /></div>
                <Tag color="neutral">{study.category}</Tag>
                <h2>{study.title}</h2>
                <p>{study.description}</p>
                <div className="tag-list">
                  {study.tags.map((tag) => <Tag color="blue" key={tag}>{tag}</Tag>)}
                </div>
                <Link className="text-link" to="/contact">
                  Explore this kind of work
                  <Icon name="arrow" size={16} />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="section section-surface">
        <div className="container proof-framework">
          <SectionHeading
            eyebrow="A useful case study should answer"
            title="What changed, and why?"
            description="As the library grows, every story will make the context, contribution, and outcome easy to understand."
          />
          <div className="framework-grid">
            {[
              ["01", "Context", "What was happening, who was affected, and what needed to improve?"],
              ["02", "Contribution", "Which Qubo capability, decision, or delivery practice made the difference?"],
              ["03", "Outcome", "What is clearer, faster, stronger, or more dependable now?"],
            ].map(([number, title, text]) => (
              <div className="framework-item" key={number}>
                <strong>{number}</strong>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <DarkCta title="Want your challenge to be the next story?" description="Let’s define a meaningful outcome first, then build the work around it." />
    </>
  );
}

const initialContactForm = {
  name: "",
  email: "",
  company: "",
  service: "",
  message: "",
};

export function ContactPage() {
  const [form, setForm] = useState(initialContactForm);
  const [status, setStatus] = useState({ type: "idle", message: "" });

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (status.type !== "idle") setStatus({ type: "idle", message: "" });
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: "error", message: "Please add your name, email, and a short message." });
      return;
    }

    const subject = form.service ? `QuboGroup enquiry: ${form.service}` : "QuboGroup enquiry";
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Company: ${form.company || "Not provided"}`,
      `Service: ${form.service || "Not selected"}`,
      "",
      form.message,
    ].join("\n");

    window.location.href = `mailto:contact@qubogroup.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setStatus({
      type: "success",
      message: "Your email app should open with the enquiry ready. If it does not, email contact@qubogroup.com directly.",
    });
  }

  return (
    <>
      <Seo title="Contact" description="Start a conversation with QuboGroup about software, infrastructure, commerce, or AI." />
      <PageHero
        eyebrow="Let’s talk"
        title="Tell us what you want to move forward."
        description="A useful first conversation can start with a question, a rough idea, or a challenge that has become impossible to ignore."
        accent="blue"
      />
      <section className="section contact-section">
        <div className="container contact-layout">
          <div className="contact-info">
            <span className="eyebrow">Contact details</span>
            <h2>We are based in Noida and work with teams wherever the work takes us.</h2>
            <p>
              Tell us a little about your context. We will route your enquiry
              to the right specialist and come back with a thoughtful next step.
            </p>
            <div className="contact-list">
              <a href="mailto:contact@qubogroup.com"><Icon name="mail" size={20} /><span><small>Email</small>contact@qubogroup.com</span></a>
              <a href="tel:+919870238757"><Icon name="phone" size={20} /><span><small>Call</small>+91 98702 38757</span></a>
              <span><Icon name="pin" size={20} /><span><small>Office</small>C-9, Maithili Marg, Sector 56, Noida, India</span></span>
            </div>
            <div className="contact-response">
              <Icon name="clock" size={18} />
              <span><strong>What happens next?</strong>We usually respond within two business days.</span>
            </div>
          </div>
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="form-heading">
              <span className="eyebrow">Start here</span>
              <h2>Send an enquiry</h2>
            </div>
            <div className="form-grid">
              <label>
                Full name <span>*</span>
                <input name="name" value={form.name} onChange={handleChange} placeholder="Your name" required />
              </label>
              <label>
                Email address <span>*</span>
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@company.com" required />
              </label>
              <label>
                Company
                <input name="company" value={form.company} onChange={handleChange} placeholder="Your organisation" />
              </label>
              <label>
                What can we help with?
                <select name="service" value={form.service} onChange={handleChange}>
                  <option value="">Select a focus</option>
                  <option>AI solutions</option>
                  <option>Technology & digital products</option>
                  <option>Infrastructure & construction</option>
                  <option>Commerce & sourcing</option>
                  <option>Something else</option>
                </select>
              </label>
              <label className="form-full">
                Message <span>*</span>
                <textarea name="message" value={form.message} onChange={handleChange} placeholder="What are you trying to achieve?" rows="6" required />
              </label>
            </div>
            <button className="button button-primary" type="submit">
              Send enquiry
              <Icon name="arrow" size={17} />
            </button>
            <p className="form-note">This form opens your email app so you can review the message before sending.</p>
            {status.type !== "idle" && (
              <p className={`form-status form-status-${status.type}`} role="status">
                <Icon name={status.type === "error" ? "close" : "check"} size={17} />
                {status.message}
              </p>
            )}
          </form>
        </div>
      </section>
      <section className="section section-surface">
        <div className="container">
          <SectionHeading
            eyebrow="Choose your starting point"
            title="Prefer to explore first?"
            description="Take a closer look at a capability, a company, or the way we think before you get in touch."
          />
          <div className="quick-link-grid">
            <Link to="/solutions/ai"><IconBadge name="brain" color="purple" /><strong>AI solutions</strong><span>Useful AI for real workflows <Icon name="arrow" size={15} /></span></Link>
            <Link to="/companies"><IconBadge name="layers" color="blue" /><strong>Our companies</strong><span>Find the right specialist <Icon name="arrow" size={15} /></span></Link>
            <Link to="/insights"><IconBadge name="spark" color="orange" /><strong>Insights</strong><span>Read how we approach the work <Icon name="arrow" size={15} /></span></Link>
          </div>
        </div>
      </section>
    </>
  );
}

export function FaqPage() {
  const [open, setOpen] = useState("Working with Qubo-0");

  return (
    <>
      <Seo title="Frequently Asked Questions" description="Answers about working with QuboGroup, AI solutions, delivery, and support." />
      <PageHero
        eyebrow="Questions, answered"
        title="A little more clarity before you begin."
        description="Here are some of the questions we hear most often. If yours is not here, we would be glad to answer it directly."
        accent="blue"
      >
        <ButtonLink to="/contact">Ask us directly</ButtonLink>
      </PageHero>
      <section className="section section-tight">
        <div className="container faq-layout">
          <div className="faq-side">
            <span className="eyebrow">FAQ</span>
            <h2>Good questions make better work.</h2>
            <p>Explore a category or start a conversation when you are ready.</p>
            <ButtonLink to="/contact" variant="dark">Get in touch</ButtonLink>
          </div>
          <div className="faq-list">
            {faqs.map((group) => (
              <div className="faq-group" key={group.category}>
                <h3>{group.category}</h3>
                {group.questions.map((item, index) => {
                  const key = `${group.category}-${index}`;
                  const isOpen = open === key;
                  return (
                    <div className={`faq-item ${isOpen ? "is-open" : ""}`} key={key}>
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => setOpen(isOpen ? "" : key)}
                      >
                        <span>{item.question}</span>
                        <Icon name="chevron" size={18} />
                      </button>
                      {isOpen && <p>{item.answer}</p>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </section>
      <DarkCta title="Still have a question?" description="There is no wrong place to start. Send us the context and we will help you find the answer." label="Ask QuboGroup" />
    </>
  );
}

export function InsightsPage() {
  return (
    <>
      <Seo title="Insights" description="Practical ideas from QuboGroup about AI, connected operations, technology, and commerce." />
      <PageHero
        eyebrow="Ideas for what comes next"
        title="Useful thinking for complex work."
        description="Short, practical perspectives on building better products, operations, and experiences across digital and physical worlds."
        accent="blue"
      >
        <ButtonLink to="/solutions/ai">Explore AI solutions</ButtonLink>
      </PageHero>
      <section className="section section-tight">
        <div className="container">
          <div className="insights-feature">
            <div className="insights-feature-art"><Icon name="brain" size={44} /></div>
            <div>
              <Tag color="purple">Featured / AI & data</Tag>
              <h2>A practical starting point for AI adoption</h2>
              <p>
                The best first AI project is usually close to a real workflow,
                easy to measure, and useful to the people doing the work every day.
              </p>
              <Link className="text-link" to="/insights/a-practical-starting-point-for-ai">Read the feature <Icon name="arrow" size={16} /></Link>
            </div>
          </div>
          <div className="insight-grid">
            {insights.slice(1).map((insight) => (
              <InsightCard insight={insight} key={insight.slug} />
            ))}
          </div>
        </div>
      </section>
      <section className="section section-surface">
        <div className="container newsletter-box">
          <div>
            <span className="eyebrow">Stay close to the work</span>
            <h2>Occasional ideas, no noise.</h2>
            <p>For updates and new perspectives, email us and we will keep you in the loop.</p>
          </div>
          <a className="button button-dark" href="mailto:contact@qubogroup.com?subject=Subscribe%20to%20QuboGroup%20insights">
            Subscribe by email
            <Icon name="arrow" size={17} />
          </a>
        </div>
      </section>
    </>
  );
}

function InsightCard({ insight }) {
  return (
    <article className="insight-card">
      <div className="insight-card-art" style={{ "--accent": insight.color }}>
        <Icon name={insight.icon} size={31} />
      </div>
      <div className="insight-meta"><span>{insight.category}</span><span>{insight.readTime}</span></div>
      <h2>{insight.title}</h2>
      <p>{insight.excerpt}</p>
      <Link className="text-link" to={`/insights/${insight.slug}`}>Read article <Icon name="arrow" size={16} /></Link>
    </article>
  );
}

export function InsightDetailPage() {
  const { slug } = useParams();
  const insight = insights.find((item) => item.slug === slug);

  if (!insight) return <Navigate to="/insights" replace />;

  return (
    <>
      <Seo title={insight.title} description={insight.excerpt} />
      <section className="article-hero">
        <div className="container article-hero-inner">
          <Link className="back-link" to="/insights"><Icon name="back" size={16} />Back to insights</Link>
          <div className="article-meta"><Tag color="blue">{insight.category}</Tag><span>{insight.date}</span><span>{insight.readTime}</span></div>
          <h1>{insight.title}</h1>
          <p>{insight.excerpt}</p>
        </div>
      </section>
      <article className="section article-content">
        <div className="container article-layout">
          <div className="article-visual" style={{ "--accent": insight.color }}><Icon name={insight.icon} size={66} /></div>
          <div className="article-body">
            {insight.sections.map((section) => (
              <section key={section.heading}>
                <h2>{section.heading}</h2>
                {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </section>
            ))}
            <div className="article-end-note">
              <Icon name="spark" size={20} />
              <p>Good ideas become more useful when they are discussed. <Link to="/contact">Start a conversation with QuboGroup.</Link></p>
            </div>
          </div>
        </div>
      </article>
    </>
  );
}

export function AboutPage() {
  return (
    <>
      <Seo title="About QuboGroup" description="Learn how QuboGroup brings technology, infrastructure, commerce, and AI together around one shared vision." />
      <PageHero
        eyebrow="About QuboGroup"
        title="A shared vision for useful progress."
        description="QuboGroup was created around a simple belief: the strongest solutions come from understanding how technology, infrastructure, commerce, and people connect."
        accent="blue"
      >
        <ButtonLink to="/companies">Meet our companies</ButtonLink>
      </PageHero>
      <section className="section section-tight">
        <div className="container story-layout">
          <div className="story-lead">
            <span className="eyebrow">Our story</span>
            <h2>Built to connect the pieces that move a business forward.</h2>
          </div>
          <div className="story-copy">
            <p>
              The work we do may look different from the outside: software,
              construction, organic commerce, and now AI. Underneath, the
              intent is the same—to make complex things clearer, more useful,
              and more dependable for the people who rely on them.
            </p>
            <p>
              We keep our companies focused, share what helps them improve,
              and bring the right mix together when a challenge crosses
              boundaries.
            </p>
          </div>
        </div>
      </section>
      <section className="section section-surface">
        <div className="container">
          <div className="stats-row">
            {stats.map((stat) => <div className="stat-card" key={stat.label}><strong>{stat.value}</strong><span>{stat.label}</span></div>)}
          </div>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <SectionHeading eyebrow="What guides us" title="Values that show up in the work." description="Principles are useful when they shape a decision, a relationship, or a detail someone else has to live with." />
          <div className="values-grid">
            {values.map((value) => (
              <article className="value-card" key={value.title} style={{ "--accent": value.color }}>
                <IconBadge name={value.icon} color="blue" />
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <DarkCta title="Want to know us through the work?" description="That is the best way. Tell us what you are trying to move forward and we will start there." />
    </>
  );
}

const benefits = [
  ["spark", "Room to grow", "Clear ownership, meaningful challenges, and space to build a career with momentum."],
  ["layers", "Learn together", "Work across disciplines and learn from people who care about their craft."],
  ["leaf", "Long-term thinking", "Build things that are useful now and still make sense as the world changes."],
  ["users", "Human culture", "Direct communication, mutual respect, and a team that makes room for different strengths."],
];

export function CareersPage() {
  return (
    <>
      <Seo title="Careers" description="Join QuboGroup and help build the future across technology, infrastructure, commerce, and AI." />
      <PageHero
        eyebrow="Build your next chapter"
        title="Bring your curiosity. Make useful things."
        description="We are building a connected group of specialist teams. If you care about thoughtful work, clear ownership, and lasting value, we would like to hear from you."
        accent="blue"
      >
        <a className="button button-primary" href="mailto:careers@qubogroup.com?subject=Career%20enquiry">
          Send your profile
          <Icon name="arrow" size={17} />
        </a>
      </PageHero>
      <section className="section section-tight">
        <div className="container">
          <SectionHeading eyebrow="Why QuboGroup" title="A place to do work you can stand behind." description="Different disciplines, one standard: care about the outcome and the people it affects." />
          <div className="benefit-grid">
            {benefits.map(([icon, title, text]) => <article className="benefit-card" key={title}><IconBadge name={icon} color="blue" /><h3>{title}</h3><p>{text}</p></article>)}
          </div>
        </div>
      </section>
      <section className="section section-surface">
        <div className="container openings-layout">
          <div>
            <span className="eyebrow">Opportunities</span>
            <h2>There is always more useful work to do.</h2>
            <p>We are growing thoughtfully. If there is not a listed role that fits, send us a note about the kind of work you want to do.</p>
          </div>
          <div className="opening-list">
            {[
              ["Product & AI", "Product thinking, AI workflows, and digital delivery", "QuboSoft"],
              ["Project delivery", "Planning, coordination, and site execution", "QuboInfra"],
              ["Commerce operations", "Sourcing, quality, and customer experience", "QuboKart"],
            ].map(([title, text, company]) => (
              <a className="opening-item" href={`mailto:careers@qubogroup.com?subject=${encodeURIComponent(`Career enquiry: ${title}`)}`} key={title}>
                <span><strong>{title}</strong><small>{text}</small></span>
                <Tag color="neutral">{company}</Tag>
                <Icon name="arrow" size={17} />
              </a>
            ))}
          </div>
        </div>
      </section>
      <DarkCta title="Do not see your role yet?" description="Send us your profile, what you want to learn, and the kind of problems you want to help solve." label="Email careers" to="mailto:careers@qubogroup.com?subject=Career%20enquiry" />
    </>
  );
}

const engagementModels = [
  ["Project-based", "A defined scope, timeline, and set of deliverables for a focused outcome.", ["Clear milestones", "Dedicated project lead", "Fixed scope to begin"]],
  ["Retained partnership", "Ongoing access to the right expertise as priorities evolve and new needs emerge.", ["Priority support", "Flexible monthly scope", "Continuous improvement"]],
  ["Connected capability", "A coordinated mix of Qubo specialists for challenges that cross digital, physical, and commerce domains.", ["One group relationship", "Specialist depth", "Shared direction"]],
];

export function WorkWithQuboPage() {
  return (
    <>
      <Seo title="Work with QuboGroup" description="Partner with QuboGroup for software, infrastructure, commerce, and AI solutions." />
      <PageHero
        eyebrow="Partnership"
        title="Let’s build something useful together."
        description="Whether you need a focused specialist or a connected team across domains, we make the path from first conversation to first result clear."
        accent="blue"
      >
        <ButtonLink to="/contact">Get started</ButtonLink>
        <ButtonLink to="/companies" variant="ghost-light">Meet the group</ButtonLink>
      </PageHero>
      <section className="section section-tight">
        <div className="container">
          <SectionHeading eyebrow="Why partner with us" title="More than a service provider." description="You get a committed partner who cares about the decisions behind the deliverable." />
          <div className="partner-grid">
            {[
              ["shield", "Trust through clarity", "We make scope, assumptions, decisions, and trade-offs visible."],
              ["spark", "Useful innovation", "We choose new tools when they solve a real problem, not because they are new."],
              ["users", "Dedicated people", "You work with people who understand the context and stay close to the outcome."],
              ["chart", "Built to grow", "We create a useful first step that can become a stronger system over time."],
            ].map(([icon, title, text]) => <article className="partner-card" key={title}><IconBadge name={icon} color="blue" /><h3>{title}</h3><p>{text}</p></article>)}
          </div>
        </div>
      </section>
      <section className="section section-surface">
        <div className="container">
          <SectionHeading eyebrow="Ways to work together" title="Choose the model that fits the moment." description="Start with the level of commitment you need today. We can adapt as the work becomes clearer." />
          <div className="engagement-grid">
            {engagementModels.map(([title, text, features], index) => (
              <article className={`engagement-card ${index === 1 ? "is-featured" : ""}`} key={title}>
                {index === 1 && <Tag color="blue">Popular starting point</Tag>}
                <span className="engagement-number">0{index + 1}</span>
                <h3>{title}</h3>
                <p>{text}</p>
                <ul className="check-list check-list-dark">{features.map((feature) => <li key={feature}><Icon name="check" size={16} />{feature}</li>)}</ul>
                <ButtonLink to="/contact" variant={index === 1 ? "primary" : "outline"} size="small">Discuss this model</ButtonLink>
              </article>
            ))}
          </div>
        </div>
      </section>
      <DarkCta title="Ready to start a conversation?" description="The first step does not need to be perfectly defined. We can shape it together." />
    </>
  );
}

export function DeepDomainPage() {
  return (
    <>
      <Seo title="Deep Domain Experience" description="Hands-on experience across software, infrastructure, commerce, and AI." />
      <PageHero eyebrow="Our expertise" title="Depth where it matters. Perspective where it helps." description="Qubo teams bring hands-on experience in building and operating real products, projects, and experiences—not just describing them." accent="blue" />
      <section className="section section-tight">
        <div className="container expertise-grid">
          {companies.map((company) => (
            <article className="expertise-card" style={{ "--accent": company.color }} key={company.slug}>
              <div className="expertise-card-heading"><IconBadge name={company.icon} color={company.slug === "qubosoft" ? "blue" : company.slug === "quboinfra" ? "green" : "orange"} /><span>{company.name}</span></div>
              <h2>{company.focus}</h2>
              <p>{company.description}</p>
              <ul className="check-list check-list-dark">{company.capabilities.map((capability) => <li key={capability}><Icon name="check" size={16} />{capability}</li>)}</ul>
              <Link className="text-link" to={`/companies/${company.slug}`}>See the detail <Icon name="arrow" size={16} /></Link>
            </article>
          ))}
        </div>
      </section>
      <section className="section section-dark">
        <div className="container centered-callout">
          <span className="eyebrow eyebrow-light">Why depth matters</span>
          <h2>Hard-won context turns into better decisions.</h2>
          <p>When a team understands the realities behind a problem, you get faster learning, fewer avoidable mistakes, and solutions that fit the way the work actually happens.</p>
        </div>
      </section>
    </>
  );
}

export function OneTeamPage() {
  return (
    <>
      <Seo title="One Team, Three Brands" description="Discover how QuboSoft, QuboInfra, and QuboKart share a group vision while keeping their specialist focus." />
      <PageHero eyebrow="Unified strength" title="One team. Three brands. A shared way of working." description="QuboSoft, QuboInfra, and QuboKart are distinct by expertise and connected by purpose, standards, and a willingness to solve across boundaries." accent="blue" />
      <section className="section section-tight">
        <div className="container connected-brands">
          <div className="brand-stack">
            {companies.map((company) => <div className="brand-stack-item" style={{ "--accent": company.color }} key={company.slug}><IconBadge name={company.icon} color={company.slug === "qubosoft" ? "blue" : company.slug === "quboinfra" ? "green" : "orange"} /><strong>{company.name}</strong><span>{company.tagline}</span></div>)}
          </div>
          <div className="shared-vision">
            <span className="eyebrow">Shared direction</span>
            <h2>Different work. The same care for the result.</h2>
            <p>We share design language, engineering culture, operating discipline, and a long-term view of the value we create.</p>
            <ButtonLink to="/work-with-qubo" variant="dark">Work with the group</ButtonLink>
          </div>
        </div>
      </section>
      <section className="section section-surface">
        <div className="container">
          <SectionHeading eyebrow="What we share" title="The connective tissue." />
          <div className="values-grid">
            {[
              ["spark", "Useful innovation", "Experiment with purpose and keep what makes the work better."],
              ["layers", "Shared learning", "Carry good decisions and hard-won lessons across the group."],
              ["users", "Collaborative spirit", "Bring the right perspectives together when the challenge needs them."],
              ["leaf", "Long-term value", "Build for the people and communities that will live with the result."],
            ].map(([icon, title, text]) => <article className="value-card" key={title}><IconBadge name={icon} color="blue" /><h3>{title}</h3><p>{text}</p></article>)}
          </div>
        </div>
      </section>
      <DarkCta title="Need the strength of more than one specialist?" description="Tell us where the challenge crosses boundaries and we will bring the right team together." />
    </>
  );
}

export function HumanOrganicPage() {
  return (
    <>
      <Seo title="Human, Organic Thinking" description="A QuboGroup philosophy for designing systems, spaces, and experiences that feel natural to use and valuable over time." />
      <PageHero eyebrow="Our philosophy" title="Human, organic thinking." description="We design systems and spaces that feel natural to use, with long-term, sustainable value at the centre of every decision." accent="green" />
      <section className="section section-tight">
        <div className="container">
          <SectionHeading eyebrow="Design principles" title="Start with people. Build for the long term." description="The best work respects the way people think, move, decide, and grow." />
          <div className="principle-grid">
            {[
              ["users", "Human-centred", "Every decision starts by understanding real needs, not technology for technology’s sake."],
              ["spark", "Intuitive by default", "The experience should make sense quickly and get out of the way of the work."],
              ["leaf", "Sustainable value", "We build for the long term, creating solutions that remain useful and responsible."],
              ["layers", "Natural flow", "Our systems and processes should work with human rhythms, not against them."],
            ].map(([icon, title, text]) => <article className="principle-card" key={title}><IconBadge name={icon} color="green" /><h3>{title}</h3><p>{text}</p></article>)}
          </div>
        </div>
      </section>
      <section className="section sustainability-section">
        <div className="container sustainability-layout">
          <div><span className="eyebrow eyebrow-light">Sustainability</span><h2>Good growth leaves something better behind.</h2><p>From responsible sourcing to thoughtful construction and technology that reduces friction, we look for ways to create value without treating the future as an afterthought.</p></div>
          <div className="sustainability-points"><div><strong>100%</strong><span>attention to context</span></div><div><strong>Long-term</strong><span>value over short-term noise</span></div><div><strong>People</strong><span>at the centre of the system</span></div></div>
        </div>
      </section>
      <DarkCta title="Build something that feels right to use." description="Bring us the people, place, or process at the centre of the challenge. We will start there." />
    </>
  );
}

export function LegalPage() {
  const { pathname } = useLocation();
  const page = legalPages[pathname] || legalPages["/privacy-policy"];

  return (
    <>
      <Seo title={page.title} description={page.description} />
      <PageHero eyebrow="QuboGroup / Legal" title={page.title} description={page.description} compact accent="blue" />
      <section className="section legal-section">
        <div className="container legal-layout">
          <div className="legal-side">
            <span>Last updated</span>
            <strong>{page.updated}</strong>
            <Link to="/contact">Questions? Contact us <Icon name="arrow" size={15} /></Link>
          </div>
          <div className="legal-content">
            {page.sections.map(([heading, text]) => <section key={heading}><h2>{heading}</h2><p>{text}</p></section>)}
          </div>
        </div>
      </section>
    </>
  );
}

export function NotFoundPage() {
  return (
    <>
      <Seo title="Page not found" description="The page you are looking for could not be found." />
      <section className="not-found">
        <div className="container not-found-inner">
          <span className="not-found-code">404</span>
          <span className="eyebrow">That path is not here</span>
          <h1>Let’s get you back to something useful.</h1>
          <p>The page may have moved, or the link may be incomplete.</p>
          <ButtonLink to="/">Back to QuboGroup</ButtonLink>
        </div>
      </section>
    </>
  );
}
